import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { formatText } from "../common/helpers";
import { MissionRequestEnumLists } from "../common/constants";
import { Button, Stack } from "@mui/material";
import { TimeView } from "../components/common/core/Time";
import MissionRequestStateChip from "../components/pages/missionrequests/common/MissionRequestStateChip";

export default function useMissionRequestsColumns(showButtons = false, onAcceptRequest = null, onRejectRequest = null, includeState = false, includeResponseMessage = false) {
  const { t } = useTranslation();

  return useMemo(() => {
    return [
      {
        field: "referenceNumber",
        type: "number",
        headerName: t("missionRequests:fields:number"),
        flex: 0,
        maxWidth: 70
      },
      {
        field: "created",
        headerName: t("common:tables:created"),
        type: "dateTime",
        valueGetter: (value) => new Date(value),
        renderCell: (params) => <TimeView time={params.row.created} />,
        flex: 0.25,
        minWidth: 90,
      },
      {
        field: "sourceType",
        headerName: t("missionRequests:fields:sourceType"),
        type: "singleSelect",
        valueGetter: (_, row) => row.sourceType,
        valueFormatter: (value) => t(`missionRequests:enums:sourceType:${MissionRequestEnumLists.SourceType[value]}`),
        valueOptions: MissionRequestEnumLists.SourceType.map((_, value) => ({
          value,
          label: t(`missionRequests:enums:sourceType:${MissionRequestEnumLists.SourceType[value]}`)
        })),
        minWidth: 80,
        flex: 0.25,
      },
      {
        field: "source.name",
        headerName: t("missionRequests:fields:source"),
        valueGetter: (_, row) => row.source?.name || "-",
        minWidth: 100,
        flex: 0.25,
      },
      {
        field: "target.carDetails.make||target.boatDetails.make||target.carDetails.model||target.boatDetails.model",
        headerName: t("missionRequests:fields:target"),
        valueGetter: (_, row) => {
          const type = row?.target?.type;
          const target = type === 1 ? row?.target?.carDetails : (type === 2 ? row.target?.boatDetails : null);
          return formatText(" ", target?.make, target?.model) || "-";
        },
        minWidth: 80,
        flex: 0.25,
        sortable: false
      },
      {
        field: "target.carDetails.registration||target.boatDetails.registration",
        headerName: t("entity:mission:target:registration"),
        valueGetter: (_, row) => {
          const type = row?.target?.type;
          const target = type === 1 ? row?.target?.carDetails : (type === 2 ? row.target?.boatDetails : null);
          return target?.registration || "-";
        },
        minWidth: 80,
        flex: 0.25,
        sortable: false
      },
      {
        field: "actors.requester.name||actors.owner.name",
        headerName: t("missionRequests:fields:requester"),
        valueGetter: (_, row) => (row.actors?.ownerIsNotRequester ? row.actors?.requester?.name : row.actors?.owner?.name) || "-",
        flex: 0.3,
        minWidth: 120,
        sortable: false
      },
      {
        field: "message",
        headerName: t("missionRequests:fields:message"),
        valueGetter: (_, row) => row?.message || "-",
        minWidth: 140,
        flex: 0.75,
      },
      {
        field: "sender",
        headerName: t("missionRequests:fields:sender"),
        valueGetter: (_, row) => row.sender || "-",
        minWidth: 100,
        flex: 0.35,
      },
      !!includeResponseMessage ? {
        field: "responseMessage",
        headerName: t("missionRequests:fields:responseMessage"),
        valueGetter: (_, row) => row?.responseMessage || "-",
        minWidth: 140,
        flex: 0.75,
      } : null,
      !!includeState ? {
        field: "response",
        headerName: t("missionRequests:fields:response"),
        type: "singleSelect",
        valueGetter: (_, row) => row.response,
        valueFormatter: (value) => t(`missionRequests:enums:responseState:${MissionRequestEnumLists.ResponseState[value]}`),
        valueOptions: MissionRequestEnumLists.SourceType.map((_, value) => ({
          value,
          label: t(`missionRequests:enums:responseState:${MissionRequestEnumLists.ResponseState[value]}`)
        })),
        renderCell: (params) => <MissionRequestStateChip response={params.value} />,
        minWidth: 80,
        flex: 0.25,
      } : null,
      !!showButtons ? {
        field: "approveOrDecline",
        type: "number",
        headerName: "",
        sortable: false,
        flex: 0.35,
        minWidth: 150,
        renderCell: (params) => {

          return (
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="inherit" size="small" onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onRejectRequest(params.id);
              }}>
                {t("missionRequests:reject")}
              </Button>
              <Button variant="contained" color="primary" size="small" onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onAcceptRequest(params.id);
              }}>
                {t("missionRequests:accept")}
              </Button>
            </Stack>
          );
        }
      } : null,
    ].filter(c => !!c)
  }, [t, showButtons, onAcceptRequest, onRejectRequest, includeState, includeResponseMessage]);
}